'use client';

import { useGetDrawResult } from '@svs/nova-poolbetting/core/frameworks/nextjs/draw';
import { type Outcome, toOutcome } from '@svs/nova-poolbetting/schema/coupon';
import type { DrawNumber } from '@svs/nova-poolbetting/schema/draw';
import { get1X2Outcome, type MultiForecast, type MultiForecastOutcome } from '@svs/nova-poolbetting/schema/forecast';
import type { Definition } from '@svs/nova-poolbetting/schema/frameworks';
import { Card } from '@svs/ui/react/card';
import type { ReactNode } from 'react';

import { MultiForecastContainer } from './MultiForecastContainer';
import { WinDivisions } from './WinDivisions';

type WinDivisionResultsContainerClientProps = {
  product: Definition['drawProducts'][number];
  drawNumber: DrawNumber;
  multiForecastOutcome?: MultiForecastOutcome;
};

function getMatchedMultiForecastWinDivisions(
  multiForecast: MultiForecast[] | undefined,
  multiForecastOutcome: MultiForecastOutcome | undefined
): MultiForecast[] | undefined {
  if (!multiForecast || multiForecastOutcome === undefined) {
    return undefined;
  }

  const outcome = toOutcome(multiForecastOutcome);
  return multiForecast.filter((forecast) => get1X2Outcome({ outcome: forecast.outcome })[0] === outcome);
}

/**
 * WinDivisionResultsContainer client component.
 *
 * @param root0 props
 * @param root0.product product for current draw
 * @param root0.drawNumber draw number for current draw
 * @param root0.multiForecastOutcome The multi-forecast outcome selected
 * @returns The WinDivisions presentational component.
 * @throws {Error} if draw data fails to load
 */
export function WinDivisionResultsContainerClient({
  product,
  drawNumber,
  multiForecastOutcome,
}: WinDivisionResultsContainerClientProps): ReactNode {
  const drawResults = useGetDrawResult({ product, drawNumber });

  let multiForecast: undefined | MultiForecast[] = undefined;
  let currentCorrectResult: undefined | Outcome = undefined;

  const { data, isError } = drawResults;

  if (data.type === 'FORECAST' && data.data.multiForecast) {
    multiForecast = data.data.multiForecast;
    const [firstForecast] = data.data.multiForecast;
    if (firstForecast) {
      const drawResult = data.data.drawResults[firstForecast.eventNumber - 1];
      currentCorrectResult = drawResult?.outcome[0];
    }
  }

  if (isError) {
    throw new Error('Failed to load draw data');
  }

  if (data.type === 'NONE') {
    return undefined;
  }

  const { drawResults: drawResultsEvents, isCancelled, winDivisions } = data.data;
  const numberOfEvents = drawResultsEvents.length;

  const matchedMultiForecastWinDivisions = getMatchedMultiForecastWinDivisions(multiForecast, multiForecastOutcome);

  const winDivisionsToUse =
    matchedMultiForecastWinDivisions && matchedMultiForecastWinDivisions.length > 0
      ? matchedMultiForecastWinDivisions
      : winDivisions;

  return (
    !isCancelled && (
      <Card className="mt-4 p-0">
        {multiForecast && (
          <MultiForecastContainer
            multiForecast={multiForecast}
            currentCorrectResult={currentCorrectResult}
            drawNumber={drawNumber}
            product={product}
            selectedOutcome={multiForecastOutcome}
          />
        )}
        <WinDivisions winDivisions={winDivisionsToUse} numberOfEvents={numberOfEvents} />
      </Card>
    )
  );
}
